import { Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";

import {
  ColDef,
  GridApi,
  GridOptions,
} from "ag-grid-community";

import { NgxUiLoaderService } from "ngx-ui-loader";

import { CustomCheckButton } from "src/app/shared/common/Checkbutton";
import { JSIGNALBUTTON } from "src/app/shared/common/JsignalButtons";

import { AlertRuleService } from "src/app/shared/services/alert-rule.service";
import { JsignalService } from "src/app/shared/services/jsignal.service";
import { LocalStorageSettingsService } from "src/app/shared/services/local-storage-settings.service";

/* =========================================================
   INTERFACES
========================================================= */

export interface AnnexDetails {
  code: string;
  name: string;

  codehierarchy?: string;

  definition: string;

  nonImdrfCode?: string;

  primaryCategeory?: string;
  secondaryCategeory?: string;

  status?: string;
  statusDescription?: string;

  hasChildren?: boolean;
  hierarchyLevel?: number;
}

interface TreeNode {
  label: string;
  key: string;

  data?: AnnexDetails;

  children?: TreeNode[];

  leaf?: boolean;
  expanded?: boolean;
}

/* =========================================================
   COMPONENT
========================================================= */

@Component({
    selector: "app-imdrf-browser",
    templateUrl: "./imdrf-browser.component.html",
    styleUrls: ["./imdrf-browser.component.scss"],
    standalone: false
})
export class ImdrfBrowserComponent implements OnInit {

  @ViewChild('dropdownRef') dropdownRef!: ElementRef;

  /* =========================================================
     UI STATE
  ========================================================= */

  mode: "select" | "browse" = "browse";

  tabs: string[] = [];

  releaseYears: string[] = [];

  activeTab: string = "";

  releaseYear: string;

  isLatestVersion = true;

  searchQuery = "";

  /* =========================================================
     TREE STATE
  ========================================================= */

  treeData: TreeNode[] = [];

  selectedAnnexDetails: AnnexDetails | null = null;

  /* =========================================================
     GRID STATE
  ========================================================= */

  filteredTableData: AnnexDetails[] = [];

  columnDefs: ColDef[] = [];

  termsGridOptions: GridOptions = {};

  gridApi!: GridApi;

  components: any;

metadataFiles: Map<string, string> = new Map(); // key: fileType, value: fileName
showMetadataModal: boolean = false;
selectedFileType: string = '';
isLoadingMetadata: boolean = false;
isDownloading: boolean = false;
showDropdown = false;

  /* =========================================================
     CONSTRUCTOR
  ========================================================= */

  constructor(
    private snackBar: MatSnackBar,
    private spinner: NgxUiLoaderService,
    private jsignalsService: JsignalService,
    private localStorageSettings: LocalStorageSettingsService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private alertRuleService: AlertRuleService,
  ) {

    this.components = {
      jsignalButton: JSIGNALBUTTON,
      headerRenderer: CustomCheckButton,
    };
  }

  /* =========================================================
     LIFECYCLE
  ========================================================= */

  ngOnInit(): void {

    console.log( "IMDRF Browser initialized with Annex:", this.activeTab, "and Release Year:", this.releaseYear );

    this.initializeGrid();

    // this.loadInitialData();
    // load release years first to populate tabs, then load data for the first tab by default
    this.loadReleaseYears(); 
  }

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  private loadInitialData(): void {
    console.log("Loading initial data for annex:", this.activeTab);
    this.loadAnnexContent(this.activeTab);
    // this.loadReleaseMetadata(this.activeTab);
  }

  /* =========================================================
     ANNEX SELECTION
  ========================================================= */

  selectTab(tab: string): void {

    this.activeTab = tab;

    console.log(`Annex ${tab} selected`);
    console.log("Selected Release Year:", this.releaseYear);

    this.clearGrid();

    this.loadInitialData();
  }

  selectLatestVersion(releaseYear: string): void {
    this.clearGrid();
    console.log(`Release year ${releaseYear} selected`);
    this.releaseYear = releaseYear;
    this.loadAnnexCodesForSelectedReleaseYear(releaseYear);
    this.loadMetadataFilesList(releaseYear);
  }

  /* =========================================================
     LOAD ANNEX TREE
  ========================================================= */

  private loadAnnexContent(annex: string): void {
    console.log(`Loading content for annex: ${annex} and release year: ${this.releaseYear}`);

    this.spinner.start();

    this.treeData = [];

    this.jsignalsService.getImdrfHierarchy(annex, this.releaseYear).subscribe({

      next: (res: AnnexDetails[]) => {

        if ((res as any)?.error) {

          this.showError((res as any).statusMessage);

          return;
        }

        this.treeData = this.buildTree(res);

        this.spinner.stop();
      },

      error: (err) => {

        console.error(err);

        this.showError(
          err?.error?.statusMessage || "Failed to load annex"
        );
      },
    });
  }

  // /* =========================================================
  //    LOAD RELEASE METADATA
  // ========================================================= */

  // private loadReleaseMetadata(annex: string): void {

  //   this.spinner.start();

  //   this.jsignalsService
  //     .getAnnexReleaseMetadata(annex)
  //     .subscribe({

  //       next: (res: any) => {

  //         if (res.error) {

  //           this.showError(res.statusMessage);

  //           return;
  //         }

  //         this.releaseYear = res[0]?.release_version;

  //         this.spinner.stop();
  //       },

  //       error: (err) => {

  //         console.error(err);

  //         this.showError(
  //           err?.error?.statusMessage || "Failed to load release metadata"
  //         );
  //       },
  //     });
  // }

  /* =========================================================
     SEARCH
  ========================================================= */

  onSearchChange(): void {

    if (!this.searchQuery?.trim()) {

      this.snackBar.open(
        "Please enter search text",
        "Ok",
        { duration: 3000 }
      );

      return;
    }

    console.log(`Searching for "${this.searchQuery}" in annex ${this.activeTab} and release year ${this.releaseYear}` );

    this.spinner.start();

    this.treeData = [];

    this.jsignalsService
      .getSearchDataFromSelectedAnnex(
        this.searchQuery,
        this.activeTab,
        this.releaseYear
      )
      .subscribe({

        next: (res: AnnexDetails[]) => {

          if ((res as any)?.error) {

            this.showError((res as any).statusMessage);

            return;
          }

          this.treeData = this.buildSearchTree(res);

          this.spinner.stop();
        },

        error: (err) => {

          console.error(err);

          this.showError(
            err?.error?.statusMessage || "Search failed"
          );
        },
      });
  }

  clearSearch(): void {

    this.searchQuery = "";

    this.loadAnnexContent(this.activeTab);
  }

  /* =========================================================
     TREE EVENTS
  ========================================================= */

  onNodeSelect(event: any): void {

    const node: TreeNode = event.node;

    if (!node?.data) {
      return;
    }

    console.log("Selected node:", node);

    const nodeCodeHierarchy = node.data.codehierarchy;

    /* ---------------------------------------------
       PARENT NODE
    --------------------------------------------- */

    if (node.data.hasChildren) {

      this.spinner.start();

      this.loadNodesByHierarchyCode(nodeCodeHierarchy).subscribe({

        next: (res: AnnexDetails[]) => {

          if ((res as any)?.error) {

            this.clearGrid();

            this.showError((res as any).statusMessage);

            return;
          }

          this.filteredTableData = res;

          this.spinner.stop();
        },

        error: (err) => {

          console.error(err);

          this.clearGrid();

          this.showError(
            err?.error?.statusMessage ||
            "Failed to load child terms"
          );
        },
      });

      return;
    }

    /* ---------------------------------------------
       LEAF NODE
    --------------------------------------------- */

    this.loadDetailsOfSelectedCode(node.data.code);
  }

  onNodeExpand(event: any): void {

    const node: TreeNode = event.node;

    if (!node?.data?.hasChildren) {
      return;
    }

    /* already loaded */
    if (node.children?.length) {
      return;
    }

    this.loadChildNodes(node);
  }

  /* =========================================================
     LAZY LOAD CHILDREN
  ========================================================= */

  private loadChildNodes(parentNode: TreeNode): void {

    const nodeCode = parentNode.data?.codehierarchy;

    if (!nodeCode) {

      console.error(
        "Missing hierarchy code for node:",
        parentNode
      );

      return;
    }

    this.spinner.start();

    this.loadNodesByHierarchyCode(nodeCode).subscribe({

      next: (res: AnnexDetails[]) => {

        if ((res as any)?.error) {

          this.showError((res as any).statusMessage);

          return;
        }

        parentNode.children = this.buildTree(res);

        parentNode.expanded = true;

        this.treeData = this.replaceNodeInTree(this.treeData, parentNode.key, parentNode.children || []);

        this.spinner.stop();
      },

      error: (err) => {

        console.error(err);

        this.showError(
          err?.error?.statusMessage ||
          "Failed to load child nodes"
        );
      },
    });
  }

  /* =========================================================
     TREE BUILDERS
  ========================================================= */

  private buildTree(records: AnnexDetails[]): TreeNode[] {

    return records.map((record) => ({

      key: record.code,

      label: `${record.code} - ${record.name}`,

      expanded: false,

      leaf: !record.hasChildren,

      children: [],

      data: record,
    }));
  }

  /**
   * Build hierarchical search tree
   * using code hierarchy path
   */
  private buildSearchTree(records: AnnexDetails[]): TreeNode[] {

    const nodeMap = new Map<string, TreeNode>();

    const roots: TreeNode[] = [];

    records.forEach((record) => {

      if (!record.codehierarchy) {
        return;
      }

      /* remove annex root */
      const hierarchy =
        record.codehierarchy.split("|").slice(1);

      let parentNode: TreeNode | null = null;

      hierarchy.forEach((code, index) => {

        const isLast =
          index === hierarchy.length - 1;

        let existingNode = nodeMap.get(code);

        if (!existingNode) {

          existingNode = {

            key: code,

            label: isLast
              ? `${record.code} - ${record.name}`
              : code,

            expanded: true,

            leaf: isLast
              ? !record.hasChildren
              : false,

            children: [],

            data: isLast
              ? record
              : null,
          };

          nodeMap.set(code, existingNode);

          /* child mapping */
          if (parentNode) {

            const exists =
              parentNode.children?.some(
                (child) => child.key === code
              );

            if (!exists) {

              parentNode.children =
                parentNode.children || [];

              parentNode.children.push(existingNode);
            }
          }

          /* root mapping */
          else {

            const rootExists =
              roots.some(
                (root) => root.key === code
              );

            if (!rootExists) {
              roots.push(existingNode);
            }
          }
        }

        parentNode = existingNode;
      });
    });

    return roots;
  }

  /* =========================================================
     GRID INITIALIZATION
  ========================================================= */

  private initializeGrid(): void {

    this.columnDefs = this.createTermsColumnDefs();

    this.termsGridOptions = {

      defaultColDef: {

        sortable: true,

        filter: true,

        resizable: true,
         filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
      },

      suppressCellFocus: true,

      suppressRowClickSelection: false,

      rowMultiSelectWithClick: true,

      pagination: true,

      paginationPageSize: 50,
    };
  }

  /* =========================================================
     GRID COLUMNS
  ========================================================= */

  private createTermsColumnDefs(): ColDef[] {

    return [

      /* checkbox */
      {
        headerName: "",

        field: "selection",

        checkboxSelection: true,

        headerCheckboxSelection: false,

        sortable: false,

        filter: false,

        resizable: false,

        width: 50,
      },

      {
        headerName: "Code",
        field: "code",
        width: 140,
        ...this.jsignalsService.autoTooltip("code"),
      },

      {
        headerName: "Term",
        field: "name",
        width: 260,
        ...this.jsignalsService.autoTooltip("Term"),
      },

      {
        headerName: "Status",
        field: "status",
        width: 180,
        ...this.jsignalsService.autoTooltip("Status"),
      },

      {
        headerName: "Status Description",
        field: "statusDescription",
        width: 240,
        ...this.jsignalsService.autoTooltip("Status Description"),
      },

      {
        headerName: "Non IMDRF Code",
        field: "nonImdrfCode",
        width: 180,
        ...this.jsignalsService.autoTooltip("Non IMDRF Code"),
      },

      {
        headerName: "Definition",
        field: "definition",
        width: 350,
        ...this.jsignalsService.autoTooltip("Definition"),
      },

      {
        headerName: "Primary Category",
        field: "primaryCategory",
        width: 220,
        ...this.jsignalsService.autoTooltip("Primary Category"),
      },

      {
        headerName: "Secondary Category",
        field: "secondaryCategory",
        width: 220,
        ...this.jsignalsService.autoTooltip("Secondary Category"),
      },
    ];
  }

  /* =========================================================
     GRID EVENTS
  ========================================================= */

  onGridReady(params: any): void {

    this.gridApi = params.api;
  }

  onGridRowSelected(event: any): void {

    if (!event.node.isSelected()) {
      return;
    }

    console.log(
      "Selected row:",
      event.data
    );
  }

  onGridSelectionChanged(): void {}

  /* =========================================================
     DATA LOADERS
  ========================================================= */

  private loadNodesByHierarchyCode(
    hierarchyCode: string
  ) {

    return this.jsignalsService
      .getImdrfHierarchy(hierarchyCode,this.releaseYear);
  }

  private loadDetailsOfSelectedCode(
    code: string
  ): void {

    this.spinner.start();

    this.jsignalsService
      .getDetailsOfSelectedAnnexCode(code, this.releaseYear)
      .subscribe({

        next: (res: any) => {

          if (res.error) {

            this.clearGrid();

            this.showError(res.statusMessage);

            return;
          }

          this.filteredTableData = [res];

          this.spinner.stop();
        },

        error: (err) => {

          console.error(err);

          this.clearGrid();

          this.showError(
            err?.error?.statusMessage ||
            "Failed to load term details"
          );
        },
      });
  }

  /* =========================================================
     HELPERS
  ========================================================= */

  private clearGrid(): void {

    this.filteredTableData = [];
  }

  private showError(message: string): void {

    this.snackBar.open(
      message,
      "Ok",
      { duration: 3000 }
    );

    this.spinner.stop();
  }

  getStatusClass(status: string): string {

    if (!status) {
      return "status-default";
    }

    const value = status.toLowerCase();

    switch (true) {

      case value.includes("not selectable"):
        return "status-warning";

      case value.includes("selectable"):
        return "status-success";

      case value.includes("modified"):
        return "status-modified";

      case value.includes("retired"):
        return "status-retired";

      case value.includes("new"):
        return "status-new";

      default:
        return "status-default";
    }
  }

  loadReleaseYears(){
     this.spinner.start();
    this.jsignalsService.getImdrfReleaseYears()
      .subscribe({

        next: (res: any) => {

          if (res.error) {

            this.clearGrid();

            this.showError(res.statusMessage);

            return;
          }

         this.releaseYears = res;
         //select first one by default 
         this.releaseYear = this.releaseYears[0];

         if(!this.releaseYear){
          this.clearGrid();
          this.showError("No release years available please refresh page and try again");
          return;
         }

         //load Annex then for that selected year
         this.loadAnnexCodesForSelectedReleaseYear(this.releaseYear);
         this.loadMetadataFilesList(this.releaseYear); 
          this.spinner.stop();
        },

        error: (err) => {

          console.error(err);

          this.clearGrid();

          this.showError(
            err?.error?.statusMessage ||
            "Failed to load release years"
          );
        },
      });
  }

  loadAnnexCodesForSelectedReleaseYear(releaseYear: any){
    console.log(`Selected release year: ${releaseYear}`); 

    if(!releaseYear){
      this.showError("Please select a release year");
      return;
    }

    this.spinner.start();
    this.jsignalsService.getAnnexCodesForSelectedReleaseYear(releaseYear)
      .subscribe({

        next: (res: any) => {

          if (res.error) {

            this.clearGrid();

            this.showError(res.statusMessage);
            this.spinner.stop();
            return;
          }

          this.tabs = res;
          this.activeTab = this.tabs[0]; // select first tab by default
          this.loadInitialData(); // load data for the first tab
            this.spinner.stop();
        },

        error: (err) => {

          console.error(err);

          this.clearGrid();
          this.spinner.stop();
          this.showError(
            err?.error?.statusMessage ||
            "Failed to load release years"
          );
        },
      });
  }


  loadMetadataFilesList(releaseYear: any) {
  if (!releaseYear) {
    this.showError("Please select a release year to load metadata files");
    return;
  }

  this.isLoadingMetadata = true;
  this.jsignalsService.getMetaDataFilesListForGIvenReleaseYear(releaseYear).subscribe({
    next: (response) => {
      // Assuming response is like: { "releaseNotes": "release_notes_2026.txt", "changeRequestLog": "changes_2026.xlsx" }
      this.metadataFiles = new Map(Object.entries(response));
      this.isLoadingMetadata = false;
      
      // Auto-show modal if there are files
      if (this.metadataFiles.size > 0) {
        console.log("Metadata files available:", this.metadataFiles);
      } else {
        this.showError('No metadata files available for this release year');
        return;
      }
    },
    error: (error) => {
      console.error('Error loading metadata files:', error);
      this.isLoadingMetadata = false;
      this.showError('Failed to load metadata files list');
    }
  });
}

// Close dropdown when clicking outside
@HostListener('document:click', ['$event'])
onDocumentClick(event: MouseEvent) {
  if (this.dropdownRef && !this.dropdownRef.nativeElement.contains(event.target)) {
    this.showDropdown = false;
  }
}

// Close on Escape key
@HostListener('document:keydown.escape')
onEscape() {
  this.showDropdown = false;
}

toggleDropdown(event: MouseEvent) {
  event.stopPropagation();
  if (!this.activeTab || !this.releaseYear || this.metadataFiles.size === 0) return;
  this.showDropdown = !this.showDropdown;
}

onMainDownloadClick() {
  if (!this.activeTab || !this.releaseYear) {
    this.showError("Please select an annex and release year to download");
    return;
  }
  if (this.metadataFiles.size === 0) return;

  // Default: download the first entry (Change Request Log usually comes first)
  const firstEntry = this.metadataFiles.entries().next().value;
  if (firstEntry) {
    this.selectAndDownload(firstEntry[0], firstEntry[1]);
  }
}

// Update your existing selectAndDownload to also close the dropdown
selectAndDownload(fileType: string, fileName: string) {
  this.selectedFileType = fileType;
  this.isDownloading = true;
  this.showDropdown = false; // close dropdown immediately on click

  this.jsignalsService.downloadMetaDataFileForImdrf(this.releaseYear, fileType).subscribe({
    next: (response) => {
      const blob = response.body;
      const contentDisposition = response.headers.get('content-disposition');
      let downloadFileName = fileName;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFileName = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      link.click();
      window.URL.revokeObjectURL(url);

      this.isDownloading = false;
      this.snackBar.open('File downloaded successfully', 'Ok', { duration: 3000 });
    },
    error: (error) => {
      console.error('Download error:', error);
      this.isDownloading = false;
      this.showError('Failed to download file');
    }
  });
}
/**
 * @Added By Rahul vk
 * @Date 2024-06-28
 * 
 * This methods added to refresh the tree. As in Prime ng 21 change the tree does 
 * not update on data change, we need to replace the node with new reference to trigger 
 * the change detection and update the tree view
 * 
 * @param nodes 
 * @param targetKey 
 * @param childNodes 
 * @returns 
 */  

  // Add this helper method to your class
private replaceNodeInTree(nodes: TreeNode[], targetKey: string, childNodes: TreeNode[]): TreeNode[] {
    return nodes.map(item => {
      if (item.key === targetKey) {
        return { ...item, children: childNodes, expanded: true, leaf: false };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: this.replaceNodeInTree(item.children, targetKey, childNodes) };
      }
      return item;
    });
  }

}